import { log } from "../../../";

import { findDOMNode } from "react-dom";
import * as React from "react";
import { observable, action, runInAction } from "mobx";
import { observer, inject } from "mobx-react";

// Material UI
import Dialog from "@material-ui/core/Dialog";

import IconButton from "@material-ui/core/Button";

// Components
import { VocabularyCardInput } from "../categoryItemCards/VocabularyCardInput";
import { ExpressionCardInput } from "../categoryItemCards/ExpressionCardInput";
import { ImagesSelector } from "./ImagesSelector";
import { CreateListsViewDialogs } from "./CreateListsViewDialogs";
import { AddButton, CreateCardsButton } from "./CreateListsViewChildren";
import CreateListViewItems from "./CreateListViewItems";
import { SingleButtonFooter } from "components/common/SingleButtonFooter";

// Stores
import { RootStore } from "stores";

// Models
import * as CustomLists from "../../../models/customLists";
import { VocabularyCategoryItem, ExpressionCategoryItem, CategoryImage } from "models";

// Localization
import { strings } from "core/localizedStrings";

// Utils
import * as Icons from "core/utils/Icons";
import * as DeviceSize from "core/utils/DeviceSize";
import { WindowResizeChangeDispatcher } from "core/utils/WindowResizeChangeDispatcher";
import { Col, Row } from "layout";
import { Fonts } from "core/utils/Fonts";
import { Colors } from "core/utils/Colors";
import Shadowbox from "components/Shadowbox";
import { Button, Grid, Paper } from "@material-ui/core";
import { CategoryEditorDetails } from "./CategoryEditorDetails";

// Layout
require("styles/commonStyles.css");

interface CreateListsViewProps extends React.Props<any> {
    rootStore: RootStore;
}

@inject("rootStore")
@observer
export class CreateListsView extends React.Component<CreateListsViewProps> {
    @observable private listViewHeight: number = 600;
    @observable private isAddingItem = false;

    private category: CustomLists.Category;

    constructor(props: CreateListsViewProps, context: any) {
        super(props, context);

        this.category = this.props.rootStore.browseListsStore.itemToEdit as CustomLists.Category;
        this.props.rootStore.createListsStore.setCurrentCategory(this.category);
    }

    public componentDidMount() {
        runInAction(() => {
            this.props.rootStore.createListsStore.rootStore.appBarTitle = strings.createSelectClassList;
        });
        WindowResizeChangeDispatcher.getInstance().addListener(this.onWindowResize);
    }

    public componentWillUnmount() {
        WindowResizeChangeDispatcher.getInstance().removeListener(this.onWindowResize);
        runInAction(() => {
            this.props.rootStore.appBarTitle = undefined;
        });
    }

    // Render Methods
    // ------------------------------------------
    render() {
        let { rootStore } = this.props;
        let { createListsStore } = rootStore;

        log.verbose("Rendering CreateListView");

        let styles = {
            container: {
                height: this.listViewHeight,
                minHeight: 300,
                maxWidth: 1000,
                margin: 5,
            },
        };

        // if (!rootStore.browseListsStore.itemToEdit || !rootStore.createListsStore.categoryItemToEdit) {
        //     rootStore.activityStore.setShouldShowCreateListsView(false);
        //     return null;
        // }

        let isImageCategory = this.category.isImageCategory();

        return (
            <div className="full-width" style={{ paddingTop: 45 }}>
                <Paper style={{ width: "100%" }}>
                    {isImageCategory ? (
                        <Grid container style={{ backgroundColor: Colors.lightGrey }}>
                            <DeviceSize.BigScreen>
                                <Grid item xs={6}>
                                    {this.renderImagesSelector()}
                                </Grid>
                                <Grid item xs={6}>
                                    <div id="listViewContainer" style={styles.container}>
                                        <Row center="xs">{this.renderTitleComponent()}</Row>
                                        <CreateListViewItems store={createListsStore} />
                                    </div>
                                </Grid>
                                <Grid
                                    item
                                    style={{ width: "100%", paddingBottom: 10, paddingLeft: 20, paddingRight: 20 }}
                                >
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color={"primary"}
                                        onClick={this.onDoneClicked}
                                    >
                                        {strings.done}
                                    </Button>
                                </Grid>
                            </DeviceSize.BigScreen>
                            <DeviceSize.SmallScreen>
                                <Grid
                                    id="listViewContainer"
                                    direction="column"
                                    justify="center"
                                    alignItems="center"
                                    style={{ width: "100%" }}
                                >
                                    <Grid item>{this.renderTitleComponent()}</Grid>
                                    <Grid item style={{ width: "100%" }}>
                                        <Grid container alignContent="flex-end" style={{ paddingBottom: 10 }}>
                                            <AddButton onClick={this.onAddCardsClicked} />
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <CreateListViewItems store={createListsStore} />
                                    </Grid>
                                </Grid>
                                {this.isAddingItem ? (
                                    this.renderImagesSelector()
                                ) : (
                                    <SingleButtonFooter text={strings.done} onClick={this.onDoneClicked} />
                                )}
                            </DeviceSize.SmallScreen>
                        </Grid>
                    ) : (
                        <Grid container direction="column">
                            <Grid item>{this.renderTitleComponent()}</Grid>
                            <Grid item>
                                {this.renderTextOnlyInput(
                                    (this.props.rootStore.browseListsStore.itemToEdit as CustomLists.Category).type
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Paper>
                <CreateListsViewDialogs rootStore={this.props.rootStore} />
                <Dialog
                    fullWidth={true}
                    open={this.props.rootStore.createListsStore.shouldShowCategoryDetailsEditor}
                    onBackdropClick={this.onEditDetailsClose}
                    onClose={this.onEditDetailsClose}
                >
                    <CategoryEditorDetails category={createListsStore.currentCategory} store={createListsStore} />
                </Dialog>
            </div>
        );
    }

    private onAddCardsClicked = () => {
        runInAction(() => {
            this.isAddingItem = true;
        });
    };

    private onDoneClicked = () => {
        let { rootStore } = this.props;
        let { browseListsStore, createListsStore, activityStore } = rootStore;

        browseListsStore.setItemToEdit(undefined);
        createListsStore.setCurrentCategory(undefined);
        createListsStore.setCategoryItemToEdit(undefined);
        activityStore.setShouldShowBrowseListsView(true);
    };

    @action
    private onWindowResize = (windowWidth: number, windowHeight: number) => {
        var listNode = findDOMNode(document.getElementById("listViewContainer")) as Element;
        if (listNode) {
            var listBoundingRect = listNode.getBoundingClientRect();
            this.listViewHeight = windowHeight - listBoundingRect.top - 110;
        }
    };

    // UI Event Handlers
    // ------------------------------------------
    @action
    private onImagesSelectionComplete = () => {
        this.isAddingItem = false;
        this.props.rootStore.createListsStore.setShouldShowImagesSelector(false);
    };

    private onEditDetailsClicked = () => {
        console.log("clicked");
        this.props.rootStore.createListsStore.shouldShowCategoryDetailsEditor = true;
    };

    // Edit cateogry details
    private onEditDetailsClose = () => {
        this.props.rootStore.createListsStore.shouldShowCategoryDetailsEditor = false;
    };

    private renderTitleComponent() {
        if (!this.category) {
            return null;
        }

        let styles = {
            container: {
                paddingBottom: 10,
                fontFamily: Fonts.BalsamiqSans,
                fontSize: 42,
            },
            iconButton: {
                paddingLeft: 5,
            },
            icon: {
                width: 24,
                height: 24,
            },
        };

        return (
            <div style={styles.container}>
                {this.category.title}
                <IconButton style={styles.iconButton} onClick={this.onEditDetailsClicked}>
                    <Icons.Edit style={styles.icon} />
                </IconButton>
            </div>
        );
    }

    private renderTextOnlyInput(type: CustomLists.CategoryType) {
        let { createListsStore } = this.props.rootStore;
        let cardContent: any;
        if (type === CustomLists.CategoryType.VocabularyTextOnly) {
            if (!createListsStore.categoryItemToEdit) {
                let categoryItem = new VocabularyCategoryItem();
                categoryItem.type = this.category.type;
                createListsStore.setCategoryItemToEdit(categoryItem);
            }
            cardContent = (
                <VocabularyCardInput categoryItem={createListsStore.categoryItemToEdit} store={createListsStore} />
            );
        } else if (type === CustomLists.CategoryType.ExpressionTextOnly) {
            if (!createListsStore.categoryItemToEdit) {
                let categoryItem = new ExpressionCategoryItem();
                categoryItem.type = this.category.type;
                createListsStore.setCategoryItemToEdit(categoryItem);
            }
            cardContent = (
                <ExpressionCardInput categoryItem={createListsStore.categoryItemToEdit} store={createListsStore} />
            );
        }

        return (
            <Grid
                container
                direction="column"
                alignItems="center"
                style={{ paddingTop: 10, backgroundColor: Colors.rgba(200, 200, 200, 0.3) }}
            >
                <Grid item>{cardContent}</Grid>
                <Grid item style={{ width: "80%" }}>
                    <CreateListViewItems store={createListsStore} />
                </Grid>
                <Grid item style={{ padding: 10, width: "100%" }}>
                    <Button fullWidth variant="contained" color={"primary"} onClick={this.onDoneClicked}>
                        {strings.done}
                    </Button>
                </Grid>
            </Grid>
        );
    }

    private renderImagesSelector() {
        let { createListsStore } = this.props.rootStore;

        let style = {
            position: "relative",
            minHeight: 300,
            maxWidth: 1000,
            margin: 5,
            padding: 5,
            border: "2px solid",
            borderRadius: 5,
        } as React.CSSProperties;

        let visibleImages = createListsStore.visibleItems.filter((item) => item instanceof CategoryImage);
        let selector = (
            <div style={style}>
                <ImagesSelector
                    store={createListsStore}
                    shouldShow={true}
                    onSelectionComplete={this.onImagesSelectionComplete}
                />
                <Button
                    disabled={visibleImages.length === 0}
                    variant="contained"
                    fullWidth
                    style={{ marginBottom: 5 }}
                    onClick={() => {
                        visibleImages.forEach((image) => {
                            createListsStore.addSelectedImage(image);
                        });
                    }}
                >
                    {strings.selectAll}
                </Button>
                <CreateCardsButton
                    store={createListsStore}
                    selectedItemsCount={createListsStore.categoryItemsToCreate.length}
                    onSelectionComplete={this.onImagesSelectionComplete}
                />
            </div>
        );

        return (
            <>
                <DeviceSize.BigScreen>{selector}</DeviceSize.BigScreen>
                <DeviceSize.SmallScreen>
                    <Shadowbox backgroundColor={Colors.lightGrey} backgroundOpacity={1}>
                        <div style={{ paddingTop: 55 }}>{selector}</div>
                    </Shadowbox>
                </DeviceSize.SmallScreen>
            </>
        );
    }
}

// function Transition(props: any) {
//     return <Slide direction="up" {...props} />;
// }

export default CreateListsView;
